import React from 'react';
import { Head, Section, SectionHeader } from '@/components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SEO_CONTENT, GENERAL_FAQ } from '@/data/constants';

interface LocationPageProps {
  location: 'cyprus' | 'london';
}

/**
 * Location-specific SEO landing page component.
 *
 * @param props - Component props.
 * @param props.location - The location to display content for.
 * @returns The location-specific landing page.
 */
const LocationPage: React.FC<LocationPageProps> = ({ location }) => {
  const content = SEO_CONTENT[location];

  /* Structured Data for Local SEO */
  const localSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Kivosystems ${location === 'london' ? 'London' : 'Cyprus'}`,
    "url": `https://www.kivosystems.com/locations/${location}`,
    "logo": "https://www.kivosystems.com/favicon.svg",
    "description": content.description,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": location === 'london' ? "London" : "Limassol",
      "addressCountry": location === 'london' ? "UK" : "CY"
    },
    // "priceRange": "£££" // Optional: helps with rich snippets
  };

  return (
    <>
      <Head
        title={content.title}
        description={content.description}
        canonicalUrl={`https://www.kivosystems.com/locations/${location}`}
        url={`https://www.kivosystems.com/locations/${location}`}
        image="/logo.svg"
        schemaOverride={localSchema}
      />

      {/* Modern Hero Section */}
      <Section className="bg-obsidian border-b border-white/5 pb-0">
        <div className="max-w-4xl relative z-10">
          <motion.span
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-[10px] font-bold uppercase tracking-[0.3em] text-sapphire-500 mb-8 block"
          >
            Regional Presence
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-6xl md:text-8xl font-display mb-10 leading-[0.85] text-white tracking-tighter"
          >
            {content.h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-400 leading-relaxed font-light mb-16 max-w-2xl"
          >
            {content.intro}
          </motion.p>
        </div>
      </Section>

      {/* Local Strategy Section */}
      <Section className="bg-obsidian">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-8">Location Philosophy</h3>
            <p className="text-2xl text-white leading-relaxed font-display tracking-tight mb-8">
              {content.body}
            </p>
            <div className="h-px w-24 bg-sapphire-500/50 mb-8" />
            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <div className="w-1.5 h-1.5 rounded-full bg-sapphire-500 mt-2" />
                <div>
                  <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-wider">{location === 'london' ? 'City of London' : 'Island-wide Coverage'}</h4>
                  <p className="text-gray-500 text-xs uppercase tracking-widest">{location === 'london' ? 'Central Hub' : 'Regional Headquarters'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-8">Specialized Services</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-12">
              {content.features.map((feature, i) => (
                <div key={feature} className="relative group">
                  <div className="text-3xl font-display text-white/10 mb-2 group-hover:text-sapphire-500/20 transition-colors">0{i + 1}</div>
                  <h4 className="text-white font-bold text-lg mb-2">{feature}</h4>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Tailored digital implementation specifically for the {location} business environment.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Global FAQ Section */}
      <Section className="bg-obsidian-50 border-t border-white/5">
        <SectionHeader label="Knowledge Base" title="Local Service FAQ" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12 max-w-6xl">
          {GENERAL_FAQ.slice(0, 4).map((faq, i) => (
            <div key={i} className="group">
              <h4 className="text-lg font-bold mb-3 text-white group-hover:text-sapphire-500 transition-colors">{faq.question}</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 flex items-center justify-between border-t border-white/5 pt-12">
          <Link to="/studio" className="group flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-gray-500 hover:text-white transition-all">
            <span>View company details</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
          </Link>
          <Link to="/contact" className="px-10 py-4 bg-white text-obsidian font-bold rounded-full hover:bg-gray-100 transition-all hover:scale-105 active:scale-95 shadow-xl">
            Start Local Project
          </Link>
        </div>
      </Section>
    </>
  );
};

export default LocationPage;
